import React, { useState, useEffect } from 'react';
import { authFetch } from '../../utils/authFetch';
import { useAuth } from '../../context/AuthContext';

interface Agent {
  id: string;
  name: string;
  email: string;
  role: string;
  isActive: boolean;
}

interface ChatAssignmentProps {
  chatId: string;
  currentAssigneeId?: string;
  onAssign: (agentId: string | null) => void;
  onClose: () => void;
}

const ChatAssignment: React.FC<ChatAssignmentProps> = ({ 
  chatId, 
  currentAssigneeId, 
  onAssign, 
  onClose 
}) => {
  const { user } = useAuth();
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(currentAssigneeId || null);
  const [availableAgents, setAvailableAgents] = useState<Agent[]>([]);
  const [loadingAgents, setLoadingAgents] = useState(true);

  // Load real agents from API
  useEffect(() => {
    const loadAgents = async () => {
      try {
        // authFetch automatically prepends /api
        const response = await authFetch('/users');
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setAvailableAgents(data.data);
          }
        }
      } catch (error) {
        console.error('Failed to load agents:', error);
        setAvailableAgents([]);
      } finally {
        setLoadingAgents(false);
      }
    };

    loadAgents();
  }, []);

  const handleAssign = () => {
    onAssign(selectedAgentId);
    onClose();
  };

  const handleUnassign = () => {
    onAssign(null);
    onClose();
  };

  const currentAssignee = availableAgents.find(a => a.id === currentAssigneeId);

  return (
    <div className="assignment-modal-overlay" onClick={onClose}>
      <div className="assignment-modal" onClick={(e) => e.stopPropagation()}>
        <div className="assignment-header">
          <h4>Assign Conversation</h4>
          <button onClick={onClose} className="close-button">×</button>
        </div>

        <div className="assignment-content">
          <div className="current-assignment">
            {currentAssigneeId && currentAssignee ? (
              <div className="assigned-to">
                <span>Currently assigned to: </span>
                <strong>{currentAssignee.name}</strong>
              </div>
            ) : (
              <div className="unassigned">
                <span>This conversation is currently unassigned</span>
              </div>
            )}
          </div>

          <div className="agent-list">
            {loadingAgents ? (
              <div style={{ padding: '20px', textAlign: 'center' }}>
                <div className="loading-spinner" style={{ margin: '0 auto 8px' }}></div>
                Loading agents...
              </div>
            ) : (
              <>
                <div className="agent-option">
                  <label className="agent-radio">
                    <input
                      type="radio"
                      name="agent"
                      value=""
                      checked={selectedAgentId === null}
                      onChange={() => setSelectedAgentId(null)}
                    />
                    <div className="agent-details">
                      <div className="agent-name">Unassigned</div>
                      <div className="agent-description">Leave conversation unassigned</div>
                    </div>
                  </label>
                </div>

                {availableAgents
                  .filter(agent => agent.isActive)
                  .map((agent) => (
                    <div key={agent.id} className="agent-option">
                      <label className="agent-radio">
                        <input
                          type="radio"
                          name="agent"
                          value={agent.id}
                          checked={selectedAgentId === agent.id}
                          onChange={() => setSelectedAgentId(agent.id)}
                        />
                        <div className="agent-details">
                          <div className="agent-name">
                            {agent.name}
                            {agent.id === user?.id && ' (You)'}
                          </div>
                          <div className="agent-description">
                            {agent.email} • {agent.role}
                          </div>
                        </div>
                      </label>
                    </div>
                  ))}
              </>
            )}
          </div>
        </div>

        <div className="assignment-actions">
          <button onClick={onClose} className="cancel-button">
            Cancel
          </button>
          {currentAssigneeId && (
            <button onClick={handleUnassign} className="unassign-button">
              Unassign
            </button>
          )}
          <button 
            onClick={handleAssign} 
            className="assign-button"
            disabled={selectedAgentId === currentAssigneeId || loadingAgents}
          >
            {selectedAgentId ? 'Assign' : 'Leave Unassigned'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatAssignment;